import React, { useState } from 'react';
import { Upload, CircleCheck as CheckCircle, CircleAlert as AlertCircle, Loader, FileText, Video } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { RegistrationData, UploadedDocument } from './types';
import { DOCUMENT_CATEGORIES } from './types';

const MAX_IMAGE_MB = 10;
const MAX_VIDEO_MB = 50;
const VIDEO_CATEGORIES = ['video_entrando_casa'];
const MULTI_ACCEPT_CATEGORIES = ['carteira_trabalho', 'holerite_1', 'holerite_2', 'holerite_3', 'comprovante_endereco'];

function isVideoCategory(cat: string) {
  return VIDEO_CATEGORIES.includes(cat);
}

function acceptFor(cat: string) {
  if (isVideoCategory(cat)) return 'video/*';
  if (MULTI_ACCEPT_CATEGORIES.includes(cat)) return 'image/*,application/pdf';
  return 'image/*';
}

function maxMb(cat: string) {
  return isVideoCategory(cat) ? MAX_VIDEO_MB : MAX_IMAGE_MB;
}

interface Props {
  data: RegistrationData;
  registrationId: string;
  slug: string;
  onComplete: (data: Partial<RegistrationData>) => void;
}

export default function DocumentsStep({ data, registrationId, slug, onComplete }: Props) {
  const [documents, setDocuments] = useState<UploadedDocument[]>(
    data.documents.length === DOCUMENT_CATEGORIES.length
      ? data.documents
      : DOCUMENT_CATEGORIES.map((cat) => {
          const existing = data.documents.find(d => d.category === cat.category);
          return existing || { ...cat, file: null, url: null, uploading: false };
        })
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const uploaded = documents.filter(d => d.url !== null).length;
  const total = documents.length;

  const handleFileChange = async (index: number, file: File) => {
    const doc = documents[index];
    const maxMbVal = maxMb(doc.category);

    if (file.size > maxMbVal * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [doc.category]: `Arquivo muito grande. Máximo ${maxMbVal}MB.` }));
      return;
    }

    setErrors(prev => { const n = { ...prev }; delete n[doc.category]; return n; });

    setDocuments(prev => prev.map((d, i) => i === index ? { ...d, uploading: true, file } : d));

    try {
      const ext = file.name.split('.').pop();
      const filePath = `registrations/${slug}/${doc.category}-${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('contract-files')
        .upload(filePath, file, { contentType: file.type, upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('contract-files').getPublicUrl(filePath);
      const url = urlData.publicUrl;

      // Save to self_registration_files
      await supabase.from('self_registration_files').insert({
        registration_id: registrationId,
        file_name: file.name,
        file_url: url,
        file_type: file.type,
        category: doc.category,
      });

      setDocuments(prev => prev.map((d, i) => i === index ? { ...d, url, uploading: false } : d));
    } catch (err) {
      console.error('Upload error:', err);
      setDocuments(prev => prev.map((d, i) => i === index ? { ...d, uploading: false } : d));
      setErrors(prev => ({ ...prev, [doc.category]: 'Erro no upload. Tente novamente.' }));
    }
  };

  const validate = () => {
    const missing = documents.filter(d => !d.url);
    if (missing.length > 0) {
      const errs: Record<string, string> = {};
      missing.forEach(d => { errs[d.category] = 'Documento obrigatório'; });
      setErrors(errs);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      onComplete({ documents });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Documentos Obrigatórios</h2>
        <p className="text-gray-600 text-sm">
          Envie todos os {total} documentos abaixo para concluir o cadastro.
        </p>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-medium text-gray-700">{uploaded} de {total} documentos</span>
          <span className={`text-sm font-semibold ${uploaded === total ? 'text-emerald-600' : 'text-gray-500'}`}>
            {Math.round((uploaded / total) * 100)}%
          </span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full transition-all duration-300 ${uploaded === total ? 'bg-emerald-500' : 'bg-blue-500'}`}
            style={{ width: `${(uploaded / total) * 100}%` }}
          />
        </div>
      </div>

      <div className="space-y-3">
        {documents.map((doc, index) => {
          const isVideo = isVideoCategory(doc.category);
          const accept = acceptFor(doc.category);
          const err = errors[doc.category];

          return (
            <div
              key={doc.category}
              className={`border-2 rounded-xl overflow-hidden transition-colors ${
                doc.url ? 'border-emerald-200 bg-emerald-50' :
                err ? 'border-red-200 bg-red-50' :
                'border-gray-100 bg-white'
              }`}
            >
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    doc.url ? 'bg-emerald-100' : 'bg-gray-100'
                  }`}>
                    {doc.uploading ? (
                      <Loader className="w-4 h-4 text-gray-400 animate-spin" />
                    ) : doc.url ? (
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    ) : isVideo ? (
                      <Video className="w-4 h-4 text-gray-400" />
                    ) : (
                      <FileText className="w-4 h-4 text-gray-400" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`text-sm font-semibold leading-tight ${doc.url ? 'text-emerald-800' : 'text-gray-800'}`}>
                          {index + 1}. {doc.label}
                        </p>
                        {doc.url && (
                          <p className="text-xs text-emerald-600 mt-0.5">Enviado com sucesso</p>
                        )}
                        {err && (
                          <p className="text-xs text-red-600 mt-0.5 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />{err}
                          </p>
                        )}
                      </div>

                      {!doc.url && !doc.uploading && (
                        <label className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg cursor-pointer hover:bg-blue-700 transition-colors whitespace-nowrap">
                          <Upload className="w-3.5 h-3.5" />
                          Enviar
                          <input
                            type="file"
                            accept={accept}
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleFileChange(index, f);
                            }}
                          />
                        </label>
                      )}

                      {doc.url && !doc.uploading && (
                        <label className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-600 text-xs font-medium rounded-lg cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap">
                          Substituir
                          <input
                            type="file"
                            accept={accept}
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleFileChange(index, f);
                            }}
                          />
                        </label>
                      )}

                      {doc.uploading && (
                        <span className="flex-shrink-0 text-xs text-gray-500 flex items-center gap-1">
                          <Loader className="w-3.5 h-3.5 animate-spin" /> Enviando...
                        </span>
                      )}
                    </div>

                    {!isVideo && (
                      <p className="text-xs text-gray-400 mt-1">
                        {MULTI_ACCEPT_CATEGORIES.includes(doc.category) ? 'Imagem ou PDF' : 'Imagem'} · Máx {maxMb(doc.category)}MB
                      </p>
                    )}
                    {isVideo && (
                      <p className="text-xs text-gray-400 mt-1">Vídeo · Máx {maxMb(doc.category)}MB</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {uploaded < total && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          Envie todos os {total - uploaded} documento(s) restante(s) para continuar.
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={saving || uploaded < total}
        className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {saving ? <><Loader className="w-5 h-5 animate-spin" /> Processando...</> : 'Continuar'}
      </button>
    </div>
  );
}
